/**
 * Hook for canned-response operations (Phase 37).
 *
 * Plain data hook (useState + useCallback) — NOT a Context provider like
 * useTickets, because the two consumers are localized: the composer picker
 * (CannedResponsePicker) needs only the picker list, and the admin screen
 * (37-05) needs the admin CRUD. Both can each call this hook independently.
 *
 * It exposes:
 *   - the grouped picker list + `loadPicker()` (idempotent — safe on picker open)
 *   - the admin category/answer lists + `loadAdminData()` and the
 *     create/update/delete/setActive wrappers (37-05 drives its UI from these)
 *
 * Mutations call the api then re-fetch the relevant admin list so local state
 * stays authoritative without the caller wiring optimistic updates.
 */

import { useCallback, useState } from 'react';
import { apiClient } from '../services/api';
import type {
  PickerCategory,
  CannedCategory,
  CannedAnswer,
  CreateCategoryInput,
  UpdateCategoryInput,
  CreateAnswerInput,
  UpdateAnswerInput,
  ImportValidateResult,
  ImportCommitSummary,
} from '../types';

type MutationResult = { ok: true } | { ok: false; error: string };

export interface UseCannedResponsesReturn {
  // ── Picker (any technician) ──────────────────────────────────────────────
  /** Grouped categories + nested active answers (the picker source). */
  pickerCategories: PickerCategory[];
  /** True while the picker list is loading. */
  isLoading: boolean;
  /** Last picker-load error message, or null. */
  error: string | null;
  /**
   * Load the grouped picker list. Idempotent and safe to call every time the
   * picker opens — always re-fetches so a freshly-edited library is reflected.
   */
  loadPicker: () => Promise<void>;

  // ── Admin (requireLevel(2) — enforced server-side) ───────────────────────
  /** Flat category list (admin). */
  adminCategories: CannedCategory[];
  /** Flat answer list INCLUDING inactive (admin). */
  adminAnswers: CannedAnswer[];
  /** True while either admin list is loading. */
  isAdminLoading: boolean;
  /** Last admin error message, or null. */
  adminError: string | null;
  /** Load both admin lists (categories + answers) in parallel. */
  loadAdminData: () => Promise<void>;

  createCategory: (body: CreateCategoryInput) => Promise<MutationResult>;
  updateCategory: (id: number, body: UpdateCategoryInput) => Promise<MutationResult>;
  deleteCategory: (id: number) => Promise<MutationResult>;
  createAnswer: (body: CreateAnswerInput) => Promise<MutationResult>;
  updateAnswer: (id: number, body: UpdateAnswerInput) => Promise<MutationResult>;
  setAnswerActive: (id: number, isActive: boolean) => Promise<MutationResult>;
  /** Hard-delete an answer (permanent). Re-fetches the admin answers on success. */
  deleteAnswer: (id: number) => Promise<MutationResult>;

  // ── CSV bulk import (37-06 backend; two-step validate → commit upsert) ────
  /**
   * The validate (dry-run) preview: per-row New / Update / will-create-category
   * / error tags + the header/row errors. Null until a file is validated.
   * Writes nothing server-side.
   */
  importPreview: ImportValidateResult | null;
  /** The commit upsert summary (created / updated / reactivated counts). */
  importSummary: ImportCommitSummary | null;
  /** True while a validate or commit request is in flight. */
  isImporting: boolean;
  /** Last import error (network / 403 / unexpected) — NOT the per-row errors. */
  importError: string | null;
  /**
   * Dry-run: upload the CSV, store the preview + row errors. Writes nothing.
   * `replace` (default false): when true, the preview's `toRemove` set lists the
   * active answers a replace-mode commit would deactivate.
   */
  validateImport: (file: File, replace?: boolean) => Promise<void>;
  /**
   * Commit the CSV (atomic upsert). On success stores the summary AND refreshes
   * the admin lists + picker so the new/updated answers + categories appear.
   * On a 422 reject, stores the returned row errors into `importPreview` so the
   * UI can re-render them. Returns the MutationResult so the caller can react.
   * `replace` (default false): mirror mode — deactivate active answers absent
   * from the file (reversible). The deactivated count rides in
   * `importSummary.removedAnswers`.
   */
  commitImport: (file: File, replace?: boolean) => Promise<MutationResult>;
  /** Trigger the CSV template download (auth-gated GET → Blob). */
  downloadTemplate: () => Promise<MutationResult>;
  /** Clear preview / summary / error between import attempts. */
  resetImport: () => void;
}

export function useCannedResponses(): UseCannedResponsesReturn {
  const [pickerCategories, setPickerCategories] = useState<PickerCategory[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [adminCategories, setAdminCategories] = useState<CannedCategory[]>([]);
  const [adminAnswers, setAdminAnswers] = useState<CannedAnswer[]>([]);
  const [isAdminLoading, setIsAdminLoading] = useState(false);
  const [adminError, setAdminError] = useState<string | null>(null);

  // ── CSV import state ─────────────────────────────────────────────────────
  const [importPreview, setImportPreview] = useState<ImportValidateResult | null>(null);
  const [importSummary, setImportSummary] = useState<ImportCommitSummary | null>(null);
  const [isImporting, setIsImporting] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);

  const loadPicker = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await apiClient.getCannedResponses();
      if (result.success && result.data) {
        setPickerCategories(Array.isArray(result.data) ? result.data : []);
      } else {
        setError(result.error || 'Failed to load canned responses');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load canned responses');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // ── Admin ──────────────────────────────────────────────────────────────
  const loadAdminData = useCallback(async () => {
    setIsAdminLoading(true);
    setAdminError(null);
    try {
      const [cats, answers] = await Promise.all([
        apiClient.listCannedCategories(),
        apiClient.listCannedAnswers(),
      ]);
      if (cats.success && cats.data) {
        setAdminCategories(Array.isArray(cats.data) ? cats.data : []);
      } else {
        setAdminError(cats.error || 'Failed to load categories');
      }
      if (answers.success && answers.data) {
        setAdminAnswers(Array.isArray(answers.data) ? answers.data : []);
      } else {
        setAdminError((prev) => prev ?? (answers.error || 'Failed to load answers'));
      }
    } catch (err) {
      setAdminError(err instanceof Error ? err.message : 'Failed to load admin data');
    } finally {
      setIsAdminLoading(false);
    }
  }, []);

  const refreshCategories = useCallback(async () => {
    const result = await apiClient.listCannedCategories();
    if (result.success && result.data) {
      setAdminCategories(Array.isArray(result.data) ? result.data : []);
    }
  }, []);

  const refreshAnswers = useCallback(async () => {
    const result = await apiClient.listCannedAnswers();
    if (result.success && result.data) {
      setAdminAnswers(Array.isArray(result.data) ? result.data : []);
    }
  }, []);

  const createCategory = useCallback(
    // Phase 38: `body` may carry caseType ('SV'|'WL'|null) — forwarded verbatim
    // to the api (omitted → General). refreshCategories() re-fetches the typed
    // CannedCategory[], so the stored category objects expose caseType.
    async (body: CreateCategoryInput): Promise<MutationResult> => {
      const result = await apiClient.createCannedCategory(body);
      if (!result.success) return { ok: false, error: result.error || 'Failed to create category' };
      await refreshCategories();
      return { ok: true };
    },
    [refreshCategories],
  );

  const updateCategory = useCallback(
    // Phase 38: caseType is threaded through the patch — 'SV'/'WL' sets, null
    // clears to General, omitting the key leaves it untouched (server rule).
    async (id: number, body: UpdateCategoryInput): Promise<MutationResult> => {
      const result = await apiClient.updateCannedCategory(id, body);
      if (!result.success) return { ok: false, error: result.error || 'Failed to update category' };
      await refreshCategories();
      return { ok: true };
    },
    [refreshCategories],
  );

  const deleteCategory = useCallback(
    async (id: number): Promise<MutationResult> => {
      const result = await apiClient.deleteCannedCategory(id);
      if (!result.success) return { ok: false, error: result.error || 'Failed to delete category' };
      await refreshCategories();
      return { ok: true };
    },
    [refreshCategories],
  );

  const createAnswer = useCallback(
    async (body: CreateAnswerInput): Promise<MutationResult> => {
      const result = await apiClient.createCannedAnswer(body);
      if (!result.success) return { ok: false, error: result.error || 'Failed to create answer' };
      await refreshAnswers();
      return { ok: true };
    },
    [refreshAnswers],
  );

  const updateAnswer = useCallback(
    async (id: number, body: UpdateAnswerInput): Promise<MutationResult> => {
      const result = await apiClient.updateCannedAnswer(id, body);
      if (!result.success) return { ok: false, error: result.error || 'Failed to update answer' };
      await refreshAnswers();
      return { ok: true };
    },
    [refreshAnswers],
  );

  const setAnswerActive = useCallback(
    async (id: number, isActive: boolean): Promise<MutationResult> => {
      const result = await apiClient.setCannedAnswerActive(id, isActive);
      if (!result.success) return { ok: false, error: result.error || 'Failed to update answer' };
      await refreshAnswers();
      return { ok: true };
    },
    [refreshAnswers],
  );

  const deleteAnswer = useCallback(
    async (id: number): Promise<MutationResult> => {
      const result = await apiClient.deleteCannedAnswer(id);
      if (!result.success) return { ok: false, error: result.error || 'Failed to delete answer' };
      await refreshAnswers();
      return { ok: true };
    },
    [refreshAnswers],
  );

  // ── CSV bulk import actions (two-step: validate dry-run → commit upsert) ──
  const resetImport = useCallback(() => {
    setImportPreview(null);
    setImportSummary(null);
    setImportError(null);
  }, []);

  const validateImport = useCallback(async (file: File, replace = false): Promise<void> => {
    setIsImporting(true);
    setImportError(null);
    setImportSummary(null);
    try {
      const result = await apiClient.validateCannedImport(file, replace);
      if (result.success && result.data) {
        setImportPreview(result.data);
      } else {
        setImportPreview(null);
        setImportError(result.error || 'Failed to validate the CSV');
      }
    } catch (err) {
      setImportPreview(null);
      setImportError(err instanceof Error ? err.message : 'Failed to validate the CSV');
    } finally {
      setIsImporting(false);
    }
  }, []);

  const commitImport = useCallback(
    async (file: File, replace = false): Promise<MutationResult> => {
      setIsImporting(true);
      setImportError(null);
      try {
        const result = await apiClient.commitCannedImport(file, replace);
        if (!result.success || !result.data) {
          const error = result.error || 'Failed to import the CSV';
          setImportError(error);
          return { ok: false, error };
        }
        const body = result.data;
        // A 422 reject comes back ok:false with row errors/preview — surface
        // them into importPreview so the UI re-renders the per-row errors.
        if (!body.ok || !body.summary) {
          setImportSummary(null);
          if (body.preview || body.errors) {
            setImportPreview({
              ok: false,
              mode: 'commit',
              preview: body.preview ?? [],
              errors: body.errors ?? [],
            });
          }
          const error =
            body.errors?.[0]?.reason || 'The CSV has errors and was not imported';
          setImportError(error);
          return { ok: false, error };
        }
        // Clean commit: store the summary + refresh the admin lists + picker so
        // the new/updated answers and any auto-created categories appear.
        setImportSummary(body.summary);
        setImportPreview(null);
        await Promise.all([loadAdminData(), loadPicker()]);
        return { ok: true };
      } catch (err) {
        const error = err instanceof Error ? err.message : 'Failed to import the CSV';
        setImportError(error);
        return { ok: false, error };
      } finally {
        setIsImporting(false);
      }
    },
    [loadAdminData, loadPicker],
  );

  const downloadTemplate = useCallback(async (): Promise<MutationResult> => {
    const result = await apiClient.downloadCannedTemplate();
    if (!result.ok) setImportError(result.error);
    return result;
  }, []);

  return {
    pickerCategories,
    isLoading,
    error,
    loadPicker,
    adminCategories,
    adminAnswers,
    isAdminLoading,
    adminError,
    loadAdminData,
    createCategory,
    updateCategory,
    deleteCategory,
    createAnswer,
    updateAnswer,
    setAnswerActive,
    deleteAnswer,
    importPreview,
    importSummary,
    isImporting,
    importError,
    validateImport,
    commitImport,
    downloadTemplate,
    resetImport,
  };
}
